/**
 * Turns BRouter engine errors into messages a planner can act on.
 *
 * The engine reports failures as plain text written for its own diagnostics, e.g.
 * "to-position not mapped in existing datafile" when a waypoint is nowhere near a
 * routable way. Those strings reach the browser unchanged, so they are translated here
 * rather than in the backend, which is not involved in this request at all.
 */

export type RoutingErrorKind =
  | 'start-not-on-network'
  | 'end-not-on-network'
  | 'no-route-found'
  | 'island'
  | 'timeout'
  | 'unreachable'
  | 'unknown';

/** Matched case-insensitively against the engine's message, first match wins. */
const PATTERNS: { pattern: RegExp; kind: RoutingErrorKind }[] = [
  { pattern: /from-position not mapped/i, kind: 'start-not-on-network' },
  { pattern: /to-position not mapped/i, kind: 'end-not-on-network' },
  { pattern: /(target|from)\s*island/i, kind: 'island' },
  { pattern: /no track found/i, kind: 'no-route-found' },
  { pattern: /time-?out|watchdog/i, kind: 'timeout' }
];

const TRANSLATION_KEYS: Record<RoutingErrorKind, string> = {
  'start-not-on-network': 'BROUTER_MAP.ERROR.START_NOT_ON_NETWORK',
  'end-not-on-network': 'BROUTER_MAP.ERROR.END_NOT_ON_NETWORK',
  'no-route-found': 'BROUTER_MAP.ERROR.NO_ROUTE_FOUND',
  island: 'BROUTER_MAP.ERROR.ISLAND',
  timeout: 'BROUTER_MAP.ERROR.TIMEOUT',
  unreachable: 'BROUTER_MAP.ERROR.UNREACHABLE',
  unknown: 'BROUTER_MAP.ERROR.UNKNOWN'
};

export const classifyRoutingError = (message: string): RoutingErrorKind => {
  const trimmed = message?.trim() ?? '';
  if (!trimmed) {
    return 'unknown';
  }

  return PATTERNS.find(({ pattern }) => pattern.test(trimmed))?.kind ?? 'unknown';
};

export const routingErrorTranslationKey = (kind: RoutingErrorKind): string =>
  TRANSLATION_KEYS[kind];

/**
 * Carries the engine's own wording alongside the classification, so the original text
 * can still be surfaced for anything we do not recognise.
 */
export class RoutingError extends Error {
  readonly kind: RoutingErrorKind;
  readonly translationKey: string;
  /** The engine's untranslated message, kept for diagnostics. */
  readonly detail: string;

  constructor(detail: string, kind: RoutingErrorKind = classifyRoutingError(detail)) {
    super(detail);
    this.name = 'RoutingError';
    this.kind = kind;
    this.detail = detail;
    this.translationKey = routingErrorTranslationKey(kind);
  }
}
